import { useRef, useState } from "react";
import {
  ActionIcon,
  Box,
  Group,
  LoadingOverlay,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import IconZoomIn from "@tabler/icons-react/dist/esm/icons/IconZoomIn.mjs";
import IconZoomOut from "@tabler/icons-react/dist/esm/icons/IconZoomOut.mjs";
import IconZoomReset from "@tabler/icons-react/dist/esm/icons/IconZoomReset.mjs";
import { useDroppable } from "@dnd-kit/core";
import { useTranslation } from "react-i18next";

import Entity from "./Entity";
import SelectEntityAccordion from "./SelectEntityAccordion";
import { useDisplayedEntitiesQuery } from "../../hooks/useDisplayedEntitiesQuery";
import { useDetachEntityMutation } from "../../hooks/mutations";
import {
  DROP_ENTITIES_TARGET,
  entitiesTargetId,
} from "../../dnd/kinds";
import classes from "./EntitiesDropTarget.module.scss";

/**
 * The entities row at the top of the query canvas: the "Add entity"
 * dropdown, the recursive list of attached entities with their connectors,
 * and a registered droppable for the optional drag-in-from-outside path.
 */
export default function EntitiesDropTarget({
  ruleId,
  queryId,
  allEntitiesData,
  canEdit = true,
  isSubquery = false,
  rightSlot,
}) {
  const { t } = useTranslation();
  const scrollRef = useRef(null);

  // CSS `zoom` (not transform) so it reflows — the scroll area + connector
  // anchors track the scaled positions. 50%–200%.
  const [zoom, setZoom] = useState(1);
  const zoomIn = () => setZoom((z) => Math.min(2, Math.round((z + 0.1) * 100) / 100));
  const zoomOut = () => setZoom((z) => Math.max(0.5, Math.round((z - 0.1) * 100) / 100));
  const clearZoom = () => setZoom(1);

  const { data: displayedEntities, isFetching } =
    useDisplayedEntitiesQuery(queryId);
  const { mutate: detachEntity } = useDetachEntityMutation({ ruleId, queryId });

  const { setNodeRef, isOver } = useDroppable({
    id: entitiesTargetId(queryId),
    data: {
      kind: DROP_ENTITIES_TARGET,
      queryId,
    },
  });

  const handleRemove = (displayedEntityId) => {
    detachEntity(displayedEntityId);
  };

  return (
    <Stack gap={4}>
      <Text size="sm" fw={600}>
        {t("rules.selectedEntities")}
      </Text>
      <Box
        ref={setNodeRef}
        className={`${classes.wrapper} ${isSubquery ? classes.subquery : ""} ${isOver ? classes.over : ""}`}
        pos="relative"
      >
        <div className={classes.headerRow}>
          <div>
            {canEdit && (
              <SelectEntityAccordion
                ruleId={ruleId}
                queryId={queryId}
                entities={allEntitiesData}
              />
            )}
          </div>
          <Group gap={4} wrap="nowrap" justify="flex-end" className={classes.rightSlot}>
            <Tooltip label="Zoom in" withArrow>
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                aria-label="Zoom in"
                onClick={zoomIn}
              >
                <IconZoomIn size={16} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Zoom out" withArrow>
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                aria-label="Zoom out"
                onClick={zoomOut}
              >
                <IconZoomOut size={16} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Clear zoom" withArrow>
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                aria-label="Clear zoom"
                onClick={clearZoom}
              >
                <IconZoomReset size={16} />
              </ActionIcon>
            </Tooltip>
            {canEdit ? rightSlot : null}
          </Group>
        </div>

        {/* ConnectorLayer listens for scroll in capture phase, so connectors
            follow this horizontal scroll without a handler here. */}
        <div ref={scrollRef} className={classes.entitiesScroll}>
          <div className={classes.zoomLayer} style={{ zoom }}>
            {displayedEntities?.map((e, i) => (
              <Entity
                key={e.diplayedEntityId ?? i}
                ruleId={ruleId}
                queryId={queryId}
                data={e.entity || e}
                name={e.name}
                diplayedEntityId={e.diplayedEntityId}
                innerDisplayedEntities={e.innerDisplayedEntities}
                from="entity-drop-target"
                allowRemove
                isTopLevel
                parentRef={scrollRef}
                onRemove={handleRemove}
                canEdit={canEdit}
              />
            ))}
          </div>
        </div>

        <LoadingOverlay
          visible={isFetching}
          overlayProps={{ backgroundOpacity: 0.4 }}
        />
      </Box>
    </Stack>
  );
}
